import { useState } from 'react';
import cx from 'classnames';
import { Icon, IconButton } from '@intellecteu/common-ui';
import { LogLineDto } from 'data';

import s from './styles.module.scss';

type LogRowProps = {
  log: LogLineDto;
};

/**
 * Strips ANSI escape codes from a string
 * ANSI escape sequences start with ESC (0x1B) followed by [ and various parameters
 */
const stripAnsiCodes = (str: string): string => {
  // Remove ANSI escape sequences: \u001B[ or \x1B[ followed by parameters and ending with a letter
  // eslint-disable-next-line no-control-regex
  return str.replace(/\u001B\[[0-9;]*[A-Za-z]/g, '').replace(/\x1B\[[0-9;]*[A-Za-z]/g, '');
};

/**
 * Extracts and parses JSON from a string that may contain ANSI codes and other text
 */
const extractJsonFromContent = (content: string): { level?: string } | null => {
  if (!content?.includes('"level"')) {
    return null;
  }

  try {
    // First, strip ANSI escape codes
    const cleanedContent = stripAnsiCodes(content);

    // Find the first occurrence of '{' which likely starts the JSON object
    const jsonStartIndex = cleanedContent.indexOf('{');
    if (jsonStartIndex === -1) {
      return null;
    }

    // Try to find the matching closing brace
    let braceCount = 0;
    let jsonEndIndex = -1;

    // eslint-disable-next-line no-plusplus
    for (let i = jsonStartIndex; i < cleanedContent.length; i++) {
      if (cleanedContent[i] === '{') {
        // eslint-disable-next-line no-plusplus
        braceCount++;
      } else if (cleanedContent[i] === '}') {
        // eslint-disable-next-line no-plusplus
        braceCount--;
        if (braceCount === 0) {
          jsonEndIndex = i + 1;
          break;
        }
      }
    }

    if (jsonEndIndex === -1) {
      return null;
    }

    // Extract and parse the JSON substring
    const jsonString = cleanedContent.substring(jsonStartIndex, jsonEndIndex);
    return JSON.parse(jsonString);
  } catch {
    // If parsing fails, return null to gracefully handle the error
    return null;
  }
};

export const LogRow = ({ log }: LogRowProps) => {
  const parsedLog = log.content ? extractJsonFromContent(log.content) : null;
  const level = parsedLog?.level?.toUpperCase();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={s.logsRow}
      onClick={() => {
        setIsOpen((prev) => !prev);
      }}
      role="presentation"
    >
      <div className={s.logsRowContainer}>
        <div className={s.logsRowExpander}>
          <IconButton
            iconName={Icon.icons.chevronDown}
            tooltipPosition="right"
            tooltipText="View log details"
            tooltipClassName={s.logsRowExpanderTooltip}
            className={s.logsRowExpanderButton}
            iconClassName={!isOpen && s.logsRowExpanderIcon}
          />
        </div>
        <div className={s.logsRowSummary}>
          <div className={s.logsRowMessage}>
            <div className={cx(s.logsRowMessageLog, isOpen && s.logsRowMessageLogExpanded)}>
              <div className={s.logsRowTimestamp}>{log.timeStamp}</div>
              {level && <div className={s.logsRowTimestamp}>[{level}]</div>}
              {log.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
